import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "cov_token";
const INVITE_KEY = "cov_invite";

export const setInviteToken = (t) => localStorage.setItem(INVITE_KEY, t);
export const getInviteToken = () => localStorage.getItem(INVITE_KEY);

axios.defaults.withCredentials = true;

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);

const auth = () => {
  const t = getToken();
  return { withCredentials: true, headers: t ? { Authorization: `Bearer ${t}` } : {} };
};

// ---------------------------------------------------------------- auth
export const unlock = async (passcode) => {
  const { data } = await axios.post(`${API}/auth/passcode`, { passcode });
  // the operator token now arrives as an httpOnly cookie, out of reach of any
  // script on the page. Purge anything a previous build left in localStorage.
  clearToken();
  return data;
};

export const exchangeSession = async (sessionId) => {
  const invite = localStorage.getItem(INVITE_KEY);
  const data = (
    await axios.post(`${API}/auth/session`, {}, {
      withCredentials: true,
      headers: {
        "X-Session-ID": sessionId,
        ...(invite ? { "X-Invite-Token": invite } : {}),
      },
    })
  ).data;
  localStorage.removeItem(INVITE_KEY);
  return data;
};

export const fetchMe = async () => (await axios.get(`${API}/auth/me`, auth())).data;

export const registerAccount = async ({ email, password, name, invite }) => {
  const { data } = await axios.post(
    `${API}/auth/register`,
    { email, password, name, invite },
    { withCredentials: true }
  );
  localStorage.removeItem(INVITE_KEY);
  return data;
};

export const loginWithPassword = async ({ email, password }) =>
  (await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true })).data;

export const requestPasswordReset = async (email) =>
  (await axios.post(`${API}/auth/forgot-password`, { email })).data;

export const resetPassword = async ({ token, password }) =>
  (await axios.post(`${API}/auth/reset-password`, { token, password }, { withCredentials: true }))
    .data;

export const logout = async () => {
  try {
    await axios.post(`${API}/auth/logout`, {}, auth());
  } finally {
    clearToken();
  }
};

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export const startGoogleLogin = () => {
  const redirectUrl = window.location.origin + "/interview";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

// ---------------------------------------------------------------- session
export const fetchSession = async () => (await axios.get(`${API}/session`, auth())).data;
export const resetSession = async () => (await axios.post(`${API}/session/reset`, {}, auth())).data;

export const pauseSitting = async () => (await axios.post(`${API}/session/pause`, {}, auth())).data;

export const resumeSitting = async () =>
  (await axios.post(`${API}/session/resume`, {}, auth())).data;
export const fetchMap = async () => (await axios.get(`${API}/map`, auth())).data;
export const fetchDebrief = async () => (await axios.get(`${API}/debrief`, auth())).data;
export const generateDebrief = async () =>
  (await axios.post(`${API}/debrief/generate`, {}, auth())).data;

// ---------------------------------------------------------------- cohort
export const fetchCohort = async () => (await axios.get(`${API}/cohort`, auth())).data;
export const recheckCohort = async () =>
  (await axios.post(`${API}/cohort/recheck`, {}, auth())).data;

// ---------------------------------------------------------------- forecast / timeline / pdf
export const fetchForecast = async () => (await axios.get(`${API}/forecast`, auth())).data;
export const fetchTimeline = async (subject) =>
  (await axios.get(`${API}/admin/timeline`, { ...auth(), params: subject ? { subject } : {} })).data;

export const downloadDossier = async () => {
  const res = await axios.get(`${API}/debrief/pdf`, { ...auth(), responseType: "blob" });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = `covalence-dossier-${new Date().toISOString().slice(0, 10)}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

// ---------------------------------------------------------------- voice
export const transcribeAudio = async (blob) => {
  const fd = new FormData();
  fd.append("audio", blob, "answer.webm");
  const { data } = await axios.post(`${API}/voice/transcribe`, fd, auth());
  return data.text;
};

export const speakText = async (text) => {
  const res = await axios.post(`${API}/voice/speak`, { text }, { ...auth(), responseType: "blob" });
  return URL.createObjectURL(res.data);
};

// ---------------------------------------------------------------- operator
export const fetchSubjects = async () => (await axios.get(`${API}/admin/subjects`, auth())).data;


export const fetchFacetBenchmarks = async () =>
  (await axios.get(`${API}/admin/facet_benchmarks`, auth())).data;

export const fetchHelp = async () => (await axios.get(`${API}/help`, auth())).data;

export const fetchTour = async (audience) =>
  (await axios.get(`${API}/help/tour`, { params: { audience }, ...auth() })).data;

export const markTourSeen = async (audience) =>
  (await axios.post(`${API}/help/tour/seen`, { audience }, auth())).data;

export const fetchOverview = async () => (await axios.get(`${API}/admin/overview`, auth())).data;

export const fetchAdminMetrics = async (subject) =>
  (await axios.get(`${API}/admin/metrics`, { ...auth(), params: subject ? { subject } : {} })).data;
export const fetchTranscript = async (subject) =>
  (await axios.get(`${API}/admin/transcript`, { ...auth(), params: subject ? { subject } : {} })).data;
export const fetchCanonical = async () => (await axios.get(`${API}/admin/canonical`, auth())).data;

export const timerAction = async (payload) =>
  (await axios.post(`${API}/admin/timer`, payload, auth())).data;
export const postDirective = async (payload) =>
  (await axios.post(`${API}/admin/directive`, payload, auth())).data;
export const fetchDirectives = async (subject) =>
  (await axios.get(`${API}/admin/directives`, { ...auth(), params: subject ? { subject } : {} })).data;
export const cancelDirective = async (id, subject) =>
  (
    await axios.delete(`${API}/admin/directive/${id}`, {
      ...auth(),
      params: subject ? { subject } : {},
    })
  ).data;
export const fetchTuning = async (subject) =>
  (await axios.get(`${API}/admin/tuning`, { ...auth(), params: subject ? { subject } : {} })).data;
export const postTuning = async (payload) =>
  (await axios.post(`${API}/admin/tuning`, payload, auth())).data;
export const fetchRhythm = async (subject) =>
  (await axios.get(`${API}/admin/rhythm`, { ...auth(), params: subject ? { subject } : {} })).data;

// ---------------------------------------------------------------- privacy
export const fetchPrivacyManifest = async () =>
  (await axios.get(`${API}/privacy/manifest`)).data;

export const fetchPrivacy = async () => (await axios.get(`${API}/privacy`, auth())).data;

export const setMaskByDefault = async (maskByDefault) =>
  (await axios.post(`${API}/privacy/preferences`, { mask_by_default: maskByDefault }, auth())).data;

export const maskMessage = async (id, masked) =>
  (await axios.post(`${API}/message/${id}/mask`, { masked }, auth())).data;

export const deleteMyData = async () => (await axios.delete(`${API}/me`, auth())).data;

// ---------------------------------------------------------------- stall / watch / replay / compare
export const pingPresence = async (typing, chars) =>
  (await axios.post(`${API}/presence`, { typing, chars }, auth())).data;

export const fetchStalls = async () => (await axios.get(`${API}/admin/stalls`, auth())).data;

export const stallOverride = async (subject, mode) =>
  (await axios.post(`${API}/admin/stall/override`, { subject, mode }, auth())).data;

export const fetchWatch = async (subject, since) =>
  (
    await axios.get(`${API}/admin/watch`, {
      ...auth(),
      params: { ...(subject ? { subject } : {}), since },
    })
  ).data;

export const fetchAdminMap = async (subject) =>
  (await axios.get(`${API}/admin/map`, { ...auth(), params: subject ? { subject } : {} })).data;

export const fetchCompare = async (a, b) =>
  (await axios.get(`${API}/admin/compare`, { ...auth(), params: { a, b } })).data;

// ---------------------------------------------------------------- instruments / avoidance / team / benchmark
export const fetchProbes = async (subject) =>
  (await axios.get(`${API}/admin/probes`, { ...auth(), params: subject ? { subject } : {} })).data;

export const banProbe = async (payload) =>
  (await axios.post(`${API}/admin/probe/ban`, payload, auth())).data;

export const liftProbeBan = async (id, subject) =>
  (
    await axios.delete(`${API}/admin/probe/ban/${id}`, {
      ...auth(),
      params: subject ? { subject } : {},
    })
  ).data;

export const vetoControl = async (payload) =>
  (await axios.post(`${API}/admin/control/veto`, payload, auth())).data;

export const fetchAvoidance = async (subject) =>
  (await axios.get(`${API}/admin/avoidance`, { ...auth(), params: subject ? { subject } : {} }))
    .data;

export const fetchPurgePlan = async () => (await axios.get(`${API}/admin/purge/plan`, auth())).data;

export const purgeTestData = async (confirm) =>
  (await axios.post(`${API}/admin/purge`, { confirm }, auth())).data;

export const fetchOperators = async () => (await axios.get(`${API}/admin/operators`, auth())).data;

export const grantOperator = async (email) =>
  (await axios.post(`${API}/admin/operators`, { email }, auth())).data;

export const revokeOperator = async (email) =>
  (await axios.delete(`${API}/admin/operators`, { ...auth(), params: { email } })).data;

export const fetchBenchmark = async (subject, minTurns) =>
  (
    await axios.get(`${API}/admin/benchmark`, {
      ...auth(),
      params: { ...(subject ? { subject } : {}), min_turns: minTurns },
    })
  ).data;

// ---------------------------------------------------------------- consent
export const fetchConsent = async () => (await axios.get(`${API}/consent`, auth())).data;

export const fetchLegal = async () => (await axios.get(`${API}/legal`)).data;

export const acceptConsent = async (manifestVersion, legalBundleVersion, documents) =>
  (
    await axios.post(
      `${API}/consent/accept`,
      {
        manifest_version: manifestVersion,
        legal_bundle_version: legalBundleVersion,
        documents,
      },
      auth()
    )
  ).data;

export const emailMyReceipt = async () =>
  (await axios.post(`${API}/consent/receipt/email`, {}, auth())).data;

export const fetchReceipt = async () => (await axios.get(`${API}/consent/receipt`, auth())).data;

export const verifyReceipt = async (receipt, signature) =>
  (await axios.post(`${API}/consent/verify`, { receipt, signature })).data;

export async function downloadReceipt() {
  const res = await axios.get(`${API}/consent/receipt/pdf`, { ...auth(), responseType: "blob" });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = "consent-receipt.pdf";
  a.click();
  URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------- snapshots / progress
export const fetchSnapshots = async (subject) =>
  (await axios.get(`${API}/admin/snapshots`, { ...auth(), params: subject ? { subject } : {} }))
    .data;

export const takeSnapshot = async (subject, label) =>
  (await axios.post(`${API}/admin/snapshot`, { subject, label }, auth())).data;

export const narrateSnapshot = async (id, subject) =>
  (
    await axios.post(
      `${API}/admin/snapshot/${id}/narrate`,
      {},
      { ...auth(), params: subject ? { subject } : {} }
    )
  ).data;

export const fetchSnapshotMap = async (id, subject) =>
  (
    await axios.get(`${API}/admin/snapshot/${id}`, {
      ...auth(),
      params: subject ? { subject } : {},
    })
  ).data;

export const fetchProgress = async (subject, a, b) =>
  (
    await axios.get(`${API}/admin/progress`, {
      ...auth(),
      params: { ...(subject ? { subject } : {}), ...(a ? { a } : {}), b: b || "now" },
    })
  ).data;

// ---------------------------------------------------------------- sealed invites
export const fetchInvites = async () => (await axios.get(`${API}/admin/invites`, auth())).data;

export const createInvite = async (label, days, email) =>
  (await axios.post(`${API}/admin/invites`, { label, days, email }, auth())).data;

export const emailInvite = async (id, email) =>
  (await axios.post(`${API}/admin/invites/${id}/email`, { email }, auth())).data;

export const probeEmail = async (email) =>
  (await axios.post(`${API}/admin/email/probe`, { email }, auth())).data;

export const revokeInvite = async (id) =>
  (await axios.delete(`${API}/admin/invites/${id}`, auth())).data;

export const lookupInvite = async (token) => (await axios.get(`${API}/invite/${token}`)).data;

// ---------------------------------------------------------------- chat stream
export async function streamChat({ text, sessionId, onDelta, onDone, onError, onPing }) {
  let completed = false;
  try {
    const t = getToken();
    const res = await fetch(`${API}/chat/stream`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        ...(t ? { Authorization: `Bearer ${t}` } : {}),
      },
      body: JSON.stringify({ text, session_id: sessionId }),
    });
    if (!res.ok || !res.body) {
      // surface what the server actually said, not a bare status code
      let detail = "";
      try {
        detail = (await res.json())?.detail || "";
      } catch {
        detail = "";
      }
      onError?.(detail || `the connection failed (${res.status})`, res.status === 409);
      return false;
    }
    const reader = res.body.getReader();
    const dec = new TextDecoder();
    let buf = "";
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buf += dec.decode(value, { stream: true });
      const frames = buf.split("\n\n");
      buf = frames.pop() ?? "";
      for (const f of frames) {
        const line = f.trim();
        if (!line.startsWith("data:")) continue;
        let evt;
        try {
          evt = JSON.parse(line.slice(5).trim());
        } catch {
          continue;
        }
        if (evt.ping !== undefined) onPing?.(evt.ping);
        if (evt.delta) onDelta?.(evt.delta);
        if (evt.error) onError?.(evt.error, evt.resumable);
        if (evt.done) {
          completed = true;
          onDone?.(evt);
        }
      }
    }
  } catch (e) {
    onError?.(e.message, true);
  }
  return completed;
}
